import { isNerTaskType, isVllmTaskType } from './taskRuntimeDefaults';

const OUTPUT_FILENAME_RE = /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/;
export const MAX_DOCKER_IMAGE_NAME_LENGTH = 100;

/** Fallback when no tasks are selected yet. */
export function defaultDockerImageName(date = new Date()) {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `model_${yyyy}-${mm}-${dd}`;
}

export function normalizeDockerImageName(raw, extension = '.tar') {
  if (raw == null) return '';
  let name = String(raw).trim();
  const ext = String(extension || '').toLowerCase();
  if (ext && name.toLowerCase().endsWith(ext)) {
    name = name.slice(0, -ext.length).trim();
  }
  return name;
}

export function validateDockerImageName(raw, extension = '.tar') {
  const name = normalizeDockerImageName(raw, extension);
  if (!name) {
    return extension === '.zip'
      ? 'Enter a name for the downloaded file.'
      : 'Enter a name for the Docker image file.';
  }
  if (name.length > MAX_DOCKER_IMAGE_NAME_LENGTH) {
    return `Name must be at most ${MAX_DOCKER_IMAGE_NAME_LENGTH} characters.`;
  }
  if (!OUTPUT_FILENAME_RE.test(name)) {
    return 'Use letters, numbers, dots, underscores, or hyphens (must start with a letter or number).';
  }
  return null;
}

function slugifyTaskName(training) {
  return (training?.usecase_name || training?.objective_name || 'task')
    .replace(/[^\w.-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
}

function formatVersionForFilename(version) {
  if (version == null) return 'v0';
  return `v${String(version)}`;
}

function formatPredictionForFilename(strategy) {
  if (strategy === 'mean') return 'average';
  if (strategy === 'max') return 'max';
  return strategy || 'average';
}

/** Matches Configure deployment sharedConfig.device. */
export function deploymentDeviceFromHardware(hardwareDevice) {
  if (!hardwareDevice) return null;
  return String(hardwareDevice).toLowerCase() === 'cpu' ? 'cpu' : 'gpu';
}

export function appendDeploymentDeviceSuffix(name, device) {
  if (!name || !device) return name || '';
  const suffix = `_${device}`;
  if (name.endsWith(suffix)) return name;
  return `${name}${suffix}`;
}

function fitNameWithDeviceSuffix(baseName, device) {
  if (!device) return baseName;
  const suffix = `_${device}`;
  if (baseName.length + suffix.length <= MAX_DOCKER_IMAGE_NAME_LENGTH) {
    return `${baseName}${suffix}`;
  }
  return `${baseName.slice(0, MAX_DOCKER_IMAGE_NAME_LENGTH - suffix.length)}${suffix}`;
}

/** Descriptive name for one task + its runtime knobs. */
export function buildTaskDockerImageName(training, runtimeConfig = {}) {
  const slug = slugifyTaskName(training);
  const version = formatVersionForFilename(training?.version);
  if (isVllmTaskType(training)) {
    return `model_${slug}_${version}`;
  }
  const longInput = runtimeConfig.long_input_processing || 'serial';
  // NER has no prediction strategy, so keep it out of the file name.
  if (isNerTaskType(training)) {
    return `model_${slug}_${version}_${longInput}`;
  }
  const prediction = formatPredictionForFilename(runtimeConfig.prediction_strategy);
  return `model_${slug}_${version}_${longInput}_${prediction}`;
}

/** Default shown in the modal from tasks, runtime config, and infrastructure. */
export function buildDefaultDockerImageName(
  selectedTasks = [],
  { hardwareDevice } = {}
) {
  const device = deploymentDeviceFromHardware(hardwareDevice);
  let base;
  if (!selectedTasks.length) {
    base = defaultDockerImageName();
  } else if (selectedTasks.length === 1) {
    const entry = selectedTasks[0];
    base = buildTaskDockerImageName(entry.training, entry.runtimeConfig);
  } else {
    const perTask = selectedTasks.map(({ training, runtimeConfig }) =>
      buildTaskDockerImageName(training, runtimeConfig)
    );
    const joined = perTask.join('_and_');
    if (joined.length <= MAX_DOCKER_IMAGE_NAME_LENGTH) {
      base = joined;
    } else {
      const bundleSuffix = `_and_${selectedTasks.length - 1}_more`;
      const first = perTask[0];
      const maxFirstLen = MAX_DOCKER_IMAGE_NAME_LENGTH - bundleSuffix.length;
      base = `${first.slice(0, maxFirstLen)}${bundleSuffix}`;
    }
  }
  return fitNameWithDeviceSuffix(base, device);
}

/** Filename sent to the backend (one tar for single- or multi-task downloads). */
export function buildDockerOutputFilename(
  baseName,
  training,
  runtimeConfig = {},
  extension = '.tar'
) {
  const base = normalizeDockerImageName(baseName, extension);
  return base || buildTaskDockerImageName(training, runtimeConfig);
}
